<template>
  <basic-modal.container
    id="basketSaveModal"
    ref="modalRef"
  >
    <basic-modal.step
      v-if="!isVerificationNeeded"
      :id="$props.mode === 'save' ? 'basketSave' : 'offerSent'"
      type="transactional"
      :step-title="$t(`basket.modal.${title}`)"
    >
      <basic-modal.content>
        <div class="basketForm">
          <p
            class="body-2-expanded"
            v-html="$t(`basket.modal.${description}`)"
          />

          <basic-form
            ref="formRef"
            class="basketForm-form"
            :name="formName"
            :validator="v"
            @cancel="$emit('cancel')"
            @submit="save"
          >
            <template v-if="$props.mode === 'save'">
              <basic-text-input
                v-model="formData.email"
                class="form__input"
                autocomplete="email"
                name="email"
                required
                theme="dark"
                type="email"
                :placeholder="$t('person.email')"
                :label="$t('person.email')"
                :v="v.email"
              />
            </template>

            <template v-else>
              <basic-text-input
                v-model="formData.firstName"
                class="form__input"
                autocomplete="given-name"
                name="firstName"
                theme="light"
                :placeholder="$t('person.firstname')"
                :label="$t('person.firstname')"
                :v="v.firstName"
              />

              <basic-text-input
                v-model="formData.lastName"
                class="form__input"
                autocomplete="family-name"
                name="lastName"
                required
                theme="light"
                :placeholder="$t('person.lastname')"
                :label="$t('person.lastname')"
                :v="v.lastName"
              />

              <basic-phone
                v-model="formData.mobile"
                class="form__input"
                name="mobile"
                required
                theme="light"
                :placeholder="$t('person.mobile')"
                :label="$t('person.mobile')"
                :v="v.mobile"
              />

              <basic-text-input
                v-model="formData.email"
                class="form__input"
                autocomplete="email"
                name="email"
                required
                theme="light"
                :placeholder="$t('person.email')"
                :label="$t('person.email')"
                :v="v.email"
              />
            </template>
          </basic-form>

          <div class="policy">
            <div
              class="title-3"
              v-text="$t(`basket.modal.basketScreenDataProtection`)"
            />

            <div
              class="body-3"
              v-html="$t(`basket.modal.${disclaimerText}`)"
            />
          </div>
        </div>
      </basic-modal.content>

      <basic-modal.footer
        :primary-label="$t(saveButtonLabel)"
        :secondary-label="$t('form.cancel')"
        @click-primary="formRef.submit()"
        @click-secondary="modalRef.close()"
      />
    </basic-modal.step>

    <basic-modal.step
      v-if="isVerificationNeeded"
      id="saveVerification"
      type="transactional"
      :step-title="$t('screen.onboarding.subtitle')"
    >
      <basic-modal.content>
        <basic-form
          ref="formRef"
          class="basketForm-form"
          :name="formName"
          :validator="v"
          @cancel="$emit('cancel')"
          @submit="save"
        >
          <tan
            v-model="formData.code"
            :mobile="formData.mobile"
            :token="basketStore.basket.reentryToken"
            :v="v.code"
          />
        </basic-form>
      </basic-modal.content>

      <basic-modal.footer
        :primary-label="$t(saveButtonLabel)"
        :secondary-label="$t('form.cancel')"
        @click-primary="formRef.submit()"
        @click-secondary="modalRef.close()"
      />
    </basic-modal.step>
  </basic-modal.container>
</template>

<script setup>
  import { computed, reactive, ref, watch } from 'vue'
  import useVuelidate from '@vuelidate/core'
  import { minLength, requiredIf } from '@vuelidate/validators'
  import cloneDeep from 'lodash/cloneDeep'

  import { events$, loading$ } from '@shared/services'
  import { DateTimeHelper } from '@shared/helpers'
  import { logError } from '@shared/helpers/Logger'
  import { updatePersonInBasket } from '@shared/utils/Person'
  import { email, firstName, lastName, mobile } from '@shared/validations/components'

  import basketStore from '@shared/store/basket'
  import useBrowser from '@shared/hooks/useBrowser'
  import useI18n from '@shared/hooks/useI18n'
  import usePerson from '@shared/hooks/usePerson'
  import usePersonDetails from '@shared/hooks/usePersonDetails'
  import useSnackbar from '@shared/hooks/useSnackbar'

  import { BasicForm, BasicPhone, BasicTextInput } from '@shared/components/Basic'
  import BasicModal from '@shared/components/Basic/Modal/index.js'
  import Tan from '@shared/components/Authentication/Tan.vue'

  import { EVENT_BASKET } from '@shared/config/events'

  // HOOKS
  const { isSmallOnlyViewport } = useBrowser()
  const { t } = useI18n()
  const { getPerson } = usePerson()
  const { getHeadOfContract } = usePersonDetails()
  const { addNotification } = useSnackbar()

  // INIT
  const emit = defineEmits(['cancel', 'saved'])
  defineOptions({
    inheritAttrs: false,
  })
  const props = defineProps({
    isSaveWithoutApplicationData: {
      type: Boolean,
      default: false,
    },

    mode: {
      type: String,
      required: true,
    },

    source: {
      type: String,
      required: true,
      validator: val => !!val,
    },
  })

  // DATA
  const isVerificationNeeded = ref(false)

  // COMPUTED
  const formData = reactive({
    email: basketStore.basket.email,
    mobile: basketStore.basket.mobile,
    firstName: null,
    lastName: null,
    code: null,
  })
  const modalRef = ref(null)
  const formRef = ref(null)

  const disclaimerText = computed(() => {
    const suffix = props.isSaveWithoutApplicationData ? '' : '_B'
    return `basketScreenDataProtectionText${suffix}`
  })

  const suggestedHeadOfContract = computed(() => {
    return (
      getPerson(getHeadOfContract()) ||
      basketStore.basket.persons.find(p => DateTimeHelper.getAge(p.personData.dateOfBirth) >= 18)
    )
  })

  const saveButtonLabel = computed(() => {
    return props.mode === 'save' || props.isSaveWithoutApplicationData ? 'form.save' : 'form.send'
  })

  const description = computed(() => {
    if (props.isSaveWithoutApplicationData) {
      return 'basketScreenModalText'
    }

    return props.mode === 'save' ? 'basketSavingInformation' : 'basketOfferteInformation'
  })

  const formName = computed(() => {
    return props.mode === 'save' ? 'save_form_tiny' : 'save_form'
  })

  const title = computed(() => {
    if (props.isSaveWithoutApplicationData) {
      return 'basketScreenModalTitle'
    }

    return props.mode === 'save' ? 'basketSavingTitle' : 'basketOfferteTitle'
  })

  // METHODS
  function getUpdatedBasket(payload) {
    // update Person
    if (payload.firstName && payload.lastName) {
      // no adults in basket
      if (!suggestedHeadOfContract.value) {
        return {
          email: payload.email,
          mobile: payload.mobile,
        }
      }
      const personData = Object.assign(suggestedHeadOfContract.value.personData, {
        firstName: payload.firstName,
        lastName: payload.lastName
      })
      const personPayload = updatePersonInBasket(cloneDeep(basketStore.basket), suggestedHeadOfContract.value.personId, {
        personData,
      })

      // set family name for all people, that haven't had it set yet
      personPayload.persons.map(person => {
        if (!person.personData.lastName) person.personData.lastName = payload.lastName
      })

      return Object.assign({}, personPayload, { email: payload.email, mobile: payload.mobile })
    } else {
      return payload
    }
  }

  async function save() {
    loading$.start({ blocking: false })

    const params = {
      emailType: !formData.mobile ? 'OFFER_REENTRY_WITHOUT_EGATE' : 'OFFER_REENTRY',
      isDesktop: !isSmallOnlyViewport.value,
    }

    if (isVerificationNeeded.value) {
      params.mobileNumberVerificationSmsTan = formData.code
    }

    try {
      const offerUpdate = !!basketStore.basket.oaBasketId
      let _basket = await getUpdatedBasket(formData)

      if (isVerificationNeeded.value === false) {
        events$.emit(EVENT_BASKET.TRACK_AFTER_UPDATE, {
          ruleId: EVENT_BASKET.SAVED,
          payload: {
            basket: _basket,
            source: props.mode === 'save' ? `${props.source}-tiny` : props.source,
            update: offerUpdate,
          },
        })
      }

      _basket = await basketStore.updateOvpBasket(_basket, {}, params)

      addNotification({
        text: t(`basket.modal.basketSaved`),
      })

      isVerificationNeeded.value = false

      loading$.end()

      return emit('saved')
    } catch (error) {
      if (error?.messages?.find(m => m.errorCode === 'MOBILE_NUMBER_VERIFICATION_REQUIRED')) {

        loading$.end()

        isVerificationNeeded.value = true
        formData.code = undefined

        v.value.$reset()
      } else {
        logError(error)
        loading$.failed()
      }
    }
  }

  // WATCHER
  watch(
    () => modalRef.value?.isModalOpen,
    val => {
      if (val) {
        formData.firstName = suggestedHeadOfContract.value?.personData.firstName ?? null
        formData.lastName = suggestedHeadOfContract.value?.personData.lastName ?? null
      }
    }
  )

  // VALIDATION
  const rules = {
    tiny: {
      email
    },
    normal: {
      email,
      firstName,
      lastName,
      mobile,
      code: {
        required: requiredIf(function () {
          return isVerificationNeeded.value
        }),
        minLength: minLength(4),
      },
    }
  }
  const v = useVuelidate(props.mode === 'save' ? rules.tiny : rules.normal, formData, { $lazy: true })

  // EPILOGUE
  defineExpose({
    modalRef,
  })
</script>

<style scoped>
.modal__forms {
  display: flex;
  flex-direction: column;
  gap: var(--fixed-spacing-fix-10);

  .modal__form {
    flex: 1 1 50%;
  }
}

.basketForm {
  display: flex;
  flex-direction: column;
  gap: var(--fixed-spacing-fix-04);

  .form__actions {
    display: flex;
    justify-content: flex-end;
  }
}

.policy {
  display: flex;
  flex-direction: column;
  gap: var(--fixed-spacing-fix-02);
  padding-bottom: var(--dotcom-responsive-spacing-res-xl);

  &:deep(a) {
    color: var(--Interaction-States-enabled-default);
    border-bottom: 1px solid var(--Interaction-States-enabled-default);
    font-weight: bold;
    cursor: pointer;
    transition-property: color, border-color;
    transition-duration: 150ms;
    transition-timing-function: ease-in;

    &:focus-visible {
      outline: 2px solid var(--on-surface);
      outline-offset: var(--fixed-spacing-fix-01);
      border-radius: var(--fixed-border-radius-fix-02);
    }

    &:hover {
      color: var(--Interaction-States-hovered-default-a);
      border-color: var(--Interaction-States-hovered-default-a);
    }
  }
}

@media (--v-medium) {
}

@media (--v-large) {
  .modal__forms {
    flex-direction: row;
  }
}

@media (--v-wide) {
}

@media (--v-ultra) {
}

@media (--v-max) {
}
</style>
